import React, { useEffect, useMemo, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { CasinoService, IChallengeItem } from 'services/casino';
import { IState } from 'appRedux/createStore';
import { selectUserId } from 'appRedux/selectors';
import { CHALLENGE_PENDING_DISMISS_KEY } from 'common/sessionKeys';
import {
  CHALLENGES_CACHE_TTL_MS,
  challengesCacheKey,
  challengesListBucket,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

// Dismissed for the current login session only — cleared on each successful login
// (see SignInAccount) so the card reappears once per session.
const DISMISS_KEY = CHALLENGE_PENDING_DISMISS_KEY;

// A challenge is a pending personal invite when the user has been given it but
// hasn't joined yet and it hasn't ended. GetChallenges drives `joined`
// (participantStatus isn't always populated on the list payload), so mirror the
// list card's rule — `joined ? 'enrolled' : 'pending'` — rather than trusting
// participantStatus alone.
const isPending = (c?: IChallengeItem) => {
  if (!c) return false;
  if (c.joined) return false;
  if ((c.participantStatus ?? '').toLowerCase() === 'enrolled') return false;
  if (c.endDate && new Date(c.endDate).getTime() < Date.now()) return false;
  return true;
};

/**
 * A gold EGM-styled card that surfaces the user's Pending (personal invited)
 * challenges at the top of the hot list. Tapping it jumps to the challenge list;
 * the X dismisses it for the rest of the session. Renders nothing when there are
 * no pending challenges or it has been dismissed.
 */
const ChallengePendingCard: React.FC = () => {
  const history = useHistory();
  const casinoSvc = useMemo(() => new CasinoService(), []);
  // GetChallenges is scoped by kpCasinoPk (same id the Challenges page uses), not
  // the casinoId used elsewhere for per-user map features.
  const kpCasinoPk = useSelector<IState>(
    (s) => s.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number | undefined;
  const isCasinoAccount = useSelector<IState>(
    (s) => s.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean | undefined;
  // undefined → false while userInfo hydrates must not re-run the fetch.
  const isCasinoAccountFlag = !!isCasinoAccount;
  const userId = useSelector(selectUserId);

  const [pending, setPending] = useState<IChallengeItem[]>([]);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!kpCasinoPk || dismissed) {
      setPending([]);
      return;
    }
    // HotListReport (and this card) unmounts/remounts on every list load
    // (first load, timeframe/insight change), so share one in-flight request and
    // a short-lived cached result instead of re-requesting on each remount.
    const key = challengesCacheKey(userId, kpCasinoPk, isCasinoAccountFlag);
    const sub = sharedPlayerScopedGet(
      challengesListBucket,
      key,
      () => casinoSvc.GetChallenges(kpCasinoPk, isCasinoAccountFlag),
      { ttlMs: CHALLENGES_CACHE_TTL_MS }
    ).subscribe({
      next: (resp) => {
        const all = [
          ...(Array.isArray(resp?.featured) ? resp.featured : []),
          ...(Array.isArray(resp?.others) ? resp.others : []),
        ];
        setPending(all.filter(isPending));
      },
      error: () => setPending([]),
    });
    return () => sub.unsubscribe();
  }, [casinoSvc, kpCasinoPk, isCasinoAccountFlag, userId, dismissed]);

  const names = useMemo(
    () => pending.map((c) => c?.name).filter((n): n is string => Boolean(n)),
    [pending]
  );

  if (dismissed || names.length === 0) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* sessionStorage unavailable — dismiss for this mount only */
    }
    setDismissed(true);
  };

  const goToChallenges = () => history.push('/tabs/challenges');

  // Duplicate the list so the vertical ticker can loop seamlessly. A single
  // pending challenge doesn't scroll.
  const shouldScroll = names.length > 1;
  const tickerNames = shouldScroll ? [...names, ...names] : names;
  // ~2.2s per name keeps a readable pace regardless of how many there are.
  const scrollDuration = `${names.length * 2.2}s`;

  return (
    <div
      className="slot-table-row challenge-pending-card"
      role="button"
      tabIndex={0}
      aria-label={`Exclusive Slot Challenge! ${names.length} pending. View challenge list.`}
      id="challenge-pending-card"
      onClick={goToChallenges}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToChallenges();
        }
      }}
    >
      <button
        type="button"
        className="challenge-pending-card__dismiss"
        aria-label="Dismiss invited challenges card"
        onClick={handleDismiss}
      >
        <IonIcon icon={close} />
      </button>

      <div className="challenge-pending-card__body">
        <span className="challenge-pending-card__title">Exclusive Slot Challenge!</span>
        <div className="challenge-pending-card__ticker">
          <div
            className={`challenge-pending-card__track${
              shouldScroll ? ' challenge-pending-card__track--scroll' : ''
            }`}
            style={shouldScroll ? { animationDuration: scrollDuration } : undefined}
          >
            {tickerNames.map((name, i) => (
              <span className="challenge-pending-card__name" key={`${name}-${i}`}>
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengePendingCard;
